/** 停机停手（屏规格 `docs/design/screens/halt.md`，#4598）。
 *
 * 全局状态读 `GET /status`（与壳层横条同源），按策略读 `GET /paper/state`（含 `halt_kind` /
 * `halt_since_utc`）；停机 `POST /halt`、停机恢复 `POST /recover`；控制事件 `GET /paper/events`；
 * 临时放行只读 `GET /risk/overrides`（申请在「人工放行」屏）；按策略拒单台账逐策略读
 * `GET /orders?strategy=<sid>&status=REJECTED`（#4752）。
 * 整体停机（`all`）遇已停机策略：跳过、其余照停（主人 2026-10-08 裁决），结果里列出跳过了谁。
 */

import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { NavLink } from "react-router";

import { readOrdersBook } from "../../api/g2";
import { isRetryable } from "../../api/errors";

import { useAction } from "../../api/mutations";
import { useStatus } from "../../api/queries";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { ts } from "../../lib/format";
import { haltKindLabel, shortUtc, type PaperStateView } from "../../lib/g1";
import { useControlEvents, useOverrides, usePaperState } from "../../lib/g1Queries";
import { haltReasonLabel, rejectedInstrument, rejectedLedger, type RejectedRow, type StrategyBook } from "../../lib/haltRejected";
import { Load } from "../overview/parts";

export function HaltScreen(): React.ReactNode {
  const status = useStatus();
  const paper = usePaperState();
  const events = useControlEvents();
  const overrides = useOverrides();
  const halted = status.data?.halted === true;
  return (
    <>
      <PageHead
        title="停机停手"
        sub="GET /status · GET /paper/state"
        actions={status.data ? <Pill tone={halted ? "halt" : "ok"}>{halted ? "停机停手" : "允许下单"}</Pill> : null}
      />
      <Lede>
        停机恢复（恢复下单）只走本屏的「停机恢复」，须留放行人与理由；临时放行（突破风险上限）不是停机恢复，申请在
        <NavLink to="/approvals">人工放行</NavLink>屏。定时触发不会恢复已停机的策略。
      </Lede>
      <div className="grid g2">
        <Card title="当前状态（全局）" sub="GET /status" cls={halted ? "fatal" : ""}>
          <Load q={status} what="系统状态">
            {(s) => (
              <>
                <div className={s.halted ? "neg" : "pos"} style={{ fontSize: 24, fontWeight: 700 }}>
                  {s.halted ? "停机停手" : "允许下单"}
                </div>
                <dl className="kv">
                  <dt>{haltReasonLabel(s.ledger_mode)}</dt>
                  <dd>{s.halt_reason ?? "—"}</dd>
                  <dt>停机起始</dt>
                  <dd>{s.halt_since_ns === null ? "—" : ts(s.halt_since_ns)}</dd>
                  <dt>放行记录</dt>
                  <dd>{s.recoveries.length ? s.recoveries.map((r) => `${r.operator}：${r.reason}`).join("；") : "无"}</dd>
                </dl>
                <Note tone={s.halted ? "bad" : ""}>停机不可自动撤销；恢复只走人工放行。全局停机 = 回测账或任一纸上盘策略停机。</Note>
              </>
            )}
          </Load>
        </Card>
        <Card title="按策略" sub="GET /paper/state" nopad>
          <Load q={paper} what="按策略停机状态">
            {(data) => <StrategyTable data={data} />}
          </Load>
        </Card>
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <HaltForm path="/halt" title="停机" options={["all", ...Object.keys(paper.data?.strategies ?? {})]} disabledIds={Object.entries(paper.data?.strategies ?? {}).filter(([, v]) => v.halted).map(([k]) => k)} offline={status.isError} />
        <HaltForm path="/recover" title="停机恢复" options={["all", ...Object.keys(paper.data?.strategies ?? {})]} disabledIds={Object.entries(paper.data?.strategies ?? {}).filter(([, v]) => !v.halted).map(([k]) => k)} offline={status.isError} confirm="恢复后将允许下单，确认已查明停机原因？" />
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="控制事件（停机 / 放行留痕）" sub="GET /paper/events" nopad>
          <Load q={events} what="控制事件">
            {(rows) => (
              <Table
                cols={[{ label: "时间", width: "22%" }, { label: "类型", width: "10%" }, { label: "策略", width: "22%" }, { label: "操作员", width: "12%" }, { label: "理由", wrap: true }]}
                rows={rows
                  .filter((row) => row.kind === "halt" || row.kind === "recover")
                  .map((row, i) => ({
                    key: `${row.ts_ns}-${i}`,
                    cls: row.kind === "halt" ? "bad" : "",
                    cells: [ts(row.ts_ns), row.kind === "halt" ? "停机" : "放行", row.strategy_id ?? "—", row.operator ?? "—", row.reason ?? "—"],
                  }))}
                empty="没有停机 / 放行事件"
              />
            )}
          </Load>
        </Card>
        <Card title="临时放行（风险上限，只读）" sub="GET /risk/overrides" nopad>
          <Load q={overrides} what="临时放行">
            {(rows) => (
              <Table
                cols={[{ label: "范围" }, { label: "操作员" }, { label: "理由", wrap: true }, { label: "到期" }]}
                rows={rows.map((row, i) => ({ key: `${row.scope}-${i}`, cells: [row.scope, row.operator, row.reason, row.expires_utc] }))}
                empty="当前没有有效的临时放行"
              />
            )}
          </Load>
          <Note>临时放行不是停机恢复；申请入口在「人工放行」屏。</Note>
        </Card>
      </div>
      <div style={{ marginTop: 12 }}>
        <RejectedCard strategies={paper.data?.strategies ?? null} />
      </div>
    </>
  );
}

/** 停机期间与超时拒单（纸上盘按策略）：每条策略最近一次纸上盘账里的拒单。 */
function RejectedCard({ strategies }: { strategies: PaperStateView["strategies"] | null }): React.ReactNode {
  const ids = Object.keys(strategies ?? {});
  const results = useQueries({
    queries: ids.map((sid) => ({
      queryKey: ["halt", "rejected", sid],
      queryFn: () => readOrdersBook({ strategy: sid }, "REJECTED"),
      retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
    })),
  });
  const sub = "GET /orders?strategy=<sid>&status=REJECTED";
  if (strategies === null) {
    return (
      <Card title="停机期间与超时拒单（按策略）" sub={sub}>
        <div className="empty">等按策略停机状态读到后再取各策略拒单</div>
      </Card>
    );
  }
  if (results.some((r) => r.isPending)) {
    return (
      <Card title="停机期间与超时拒单（按策略）" sub={sub}>
        <div className="empty">加载中…</div>
      </Card>
    );
  }
  let rows: RejectedRow[];
  try {
    rows = rejectedLedger(
      results.map((r, i): StrategyBook => (r.isError ? { strategy: ids[i] ?? "", error: r.error } : { strategy: ids[i] ?? "", book: r.data! })),
    );
  } catch (error) {
    return (
      <Card title="停机期间与超时拒单（按策略）" sub={sub}>
        <Note tone="bad">{error instanceof Error ? error.message : String(error)}</Note>
        <button type="button" className="btn" onClick={() => results.forEach((r) => void r.refetch())}>
          重试
        </button>
      </Card>
    );
  }
  return (
    <Card title="停机期间与超时拒单（按策略）" sub={sub} nopad>
      <Table
        cols={[{ label: "时间", width: "20%" }, { label: "策略" }, { label: "订单", mono: true }, { label: "标的" }, { label: "原因", wrap: true }, { label: "账（运行）", mono: true }]}
        rows={rows.map((row) => ({
          key: `${row.strategy_id}-${row.client_order_id}`,
          cls: row.timeout ? "" : "bad",
          cells: [
            ts(row.ts_event),
            row.strategy_id,
            row.client_order_id,
            <InstrumentCell id={row.instrument_id} />,
            row.timeout && strategies[row.strategy_id]?.halted !== true ? `${row.reason ?? "—"}（纸上盘委托超时只记台账，未停机）` : (row.reason ?? "—"),
            row.run_id ?? "—",
          ],
        }))}
        empty="没有拒单"
      />
      <Note>每条策略取最近一次纸上盘运行的账（累计回放，含历次拒单）；还没有纸上盘运行的策略不列。当前回测账的拒单见「订单」屏。</Note>
    </Card>
  );
}

function InstrumentCell({ id }: { id: string }): React.ReactNode {
  const cell = rejectedInstrument(id);
  return cell.missing ? <span className="neg" title="旧账本的风控拒单没有记标的">{cell.text}</span> : cell.text;
}

function StrategyTable({ data }: { data: PaperStateView }): React.ReactNode {
  return (
    <Table
      cols={[{ label: "策略" }, { label: "状态" }, { label: "停机种类" }, { label: "进入停机" }, { label: "原因", wrap: true }, { label: "最后交易日" }]}
      rows={Object.entries(data.strategies).map(([id, item]) => ({
        key: id,
        cls: item.halted ? "bad" : "",
        cells: [
          id,
          item.halted ? <Pill tone="halt">停机停手</Pill> : <Pill tone="ok">允许下单</Pill>,
          item.halted ? haltKindLabel(item.halt_kind) : "—",
          item.halted ? shortUtc(item.halt_since_utc) : "—",
          item.halt_reason ?? "—",
          item.last_trade_date ?? "—",
        ],
      }))}
      empty="没有策略"
    />
  );
}

function HaltForm({
  path,
  title,
  options,
  disabledIds,
  offline,
  confirm,
}: {
  path: "/halt" | "/recover";
  title: string;
  options: string[];
  disabledIds: string[];
  offline: boolean;
  confirm?: string;
}): React.ReactNode {
  const action = useAction(path);
  const [target, setTarget] = useState("");
  const [operator, setOperator] = useState("");
  const [reason, setReason] = useState("");
  const missing = !target || !operator.trim() || !reason.trim();
  const result = action.data as { halted_now?: string[]; skipped?: string[]; recoveries?: { strategy_id: string }[] } | undefined;
  return (
    <Card title={title} sub={`POST ${path} · 服务端作最终判定`}>
      <div className="fieldrow">
        <label className="f">
          <span>strategy_id</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">请选择</option>
            {options.map((option) => (
              <option key={option} value={option} disabled={option !== "all" && disabledIds.includes(option)}>
                {option}
                {option !== "all" && disabledIds.includes(option) ? (path === "/halt" ? "（已停机）" : "（未停机）") : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          <span>operator</span>
          <input value={operator} onChange={(e) => setOperator(e.target.value)} />
        </label>
        <label className="f">
          <span>reason</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </label>
      </div>
      <button
        type="button"
        className="btn primary"
        disabled={missing || offline || action.isPending}
        onClick={() => {
          if (confirm && !window.confirm(confirm)) return;
          action.mutate({ strategy_id: target, operator: operator.trim(), reason: reason.trim() });
        }}
      >
        {action.isPending ? "提交中…" : "提交"}
      </button>
      {offline ? <Note tone="bad">后端不可达，无法确认当前状态</Note> : null}
      {path === "/halt" ? <Note>选 all 时已停机的策略会被跳过、其余照停（原停机原因保留）。</Note> : null}
      {action.isError ? <Note tone="bad">{action.error instanceof Error ? action.error.message : String(action.error)}</Note> : null}
      {action.isSuccess ? (
        <Note>
          服务端已受理。
          {result?.halted_now ? ` 本次停机：${result.halted_now.join("、") || "—"}。` : ""}
          {result?.skipped?.length ? ` 已停机而跳过：${result.skipped.join("、")}。` : ""}
          {result?.recoveries ? ` 本次放行：${result.recoveries.map((r) => r.strategy_id).join("、") || "—"}。` : ""}
        </Note>
      ) : null}
    </Card>
  );
}
